import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { House, List as MenuIcon, X } from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";
import AppDownloadRibbon from "@/components/AppDownloadRibbon";

const NAV_LINKS = [
  { to: "/features", label: "Features" },
  { to: "/try-demo", label: "Try the demo" },
  { to: "/pricing", label: "Pricing" },
  { to: "/about", label: "About" },
  { to: "/blog", label: "Blog" },
  { to: "/faq", label: "FAQ" },
  { to: "/contact", label: "Contact" },
];

export default function MarketingLayout({ children }) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const location = useLocation();
  useEffect(() => { setOpen(false); }, [location.pathname]);

  return (
    <div className="min-h-screen bg-sand-100 flex flex-col">
      <AppDownloadRibbon />
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-sand-100/85 border-b border-sand-300" data-testid="marketing-header">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-4 flex items-center justify-between gap-6">
          <Link to="/" className="flex items-center gap-3 shrink-0" data-testid="logo-home-link">
            <div className="h-10 w-10 rounded-2xl bg-terracotta-500 text-white grid place-items-center shadow-ambient">
              <House weight="duotone" size={22} />
            </div>
            <div className="leading-tight">
              <div className="font-heading text-lg font-semibold text-stoneink-900">Nestled</div>
              <div className="text-xs text-stoneink-500 -mt-0.5">a place for everything</div>
            </div>
          </Link>

          <nav className="hidden lg:flex items-center gap-1" data-testid="marketing-nav">
            {NAV_LINKS.map((l) => (
              <NavLink key={l.to} to={l.to} data-testid={`nav-${l.to.slice(1)}`}
                className={({ isActive }) => `px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  isActive ? "text-terracotta-600 bg-terracotta-50" : "text-stoneink-700 hover:bg-sand-200"
                }`}>
                {l.label}
              </NavLink>
            ))}
          </nav>

          <div className="hidden lg:flex items-center gap-2">
            <Link to="/beta" data-testid="cta-beta"
              className="group h-10 px-3 rounded-xl border border-sage-400/40 bg-sage-400/10 hover:bg-sage-400/20 hover:border-sage-400 text-stoneink-900 text-sm font-medium flex items-center gap-2 transition-colors">
              <span className="relative flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-sage-500 opacity-70"></span>
                <span className="relative inline-flex h-2 w-2 rounded-full bg-sage-500"></span>
              </span>
              Try the app
              <span className="text-[9px] font-bold uppercase tracking-[0.14em] text-sage-600">Beta</span>
            </Link>
            {user ? (
              <Link to="/" data-testid="cta-dashboard"
                className="h-10 px-4 rounded-xl bg-terracotta-500 hover:bg-terracotta-600 text-white text-sm font-medium flex items-center transition-colors">
                Go to dashboard →
              </Link>
            ) : (
              <>
                <Link to="/login" data-testid="cta-signin" className="h-10 px-4 rounded-xl text-stoneink-700 hover:bg-sand-200 text-sm font-medium flex items-center">Sign in</Link>
                <Link to="/register" data-testid="cta-getstarted" className="h-10 px-4 rounded-xl bg-terracotta-500 hover:bg-terracotta-600 text-white text-sm font-medium flex items-center">Get started</Link>
              </>
            )}
          </div>

          <button onClick={() => setOpen(!open)} data-testid="mobile-menu-btn"
            className="lg:hidden h-10 w-10 grid place-items-center rounded-xl hover:bg-sand-200 text-stoneink-700">
            {open ? <X size={22} /> : <MenuIcon size={22} />}
          </button>
        </div>

        {open && (
          <div className="lg:hidden border-t border-sand-300 bg-sand-100" data-testid="mobile-menu">
            <div className="max-w-7xl mx-auto px-6 py-4 flex flex-col gap-1">
              {NAV_LINKS.map((l) => (
                <Link key={l.to} to={l.to} className="px-4 py-3 rounded-xl hover:bg-sand-200 text-stoneink-900 font-medium">{l.label}</Link>
              ))}
              <Link to="/beta" data-testid="mobile-nav-beta"
                className="px-4 py-3 rounded-xl border border-sage-400/40 bg-sage-400/10 text-stoneink-900 font-medium flex items-center gap-2">
                <span className="relative flex h-2 w-2">
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-sage-500 opacity-70"></span>
                  <span className="relative inline-flex h-2 w-2 rounded-full bg-sage-500"></span>
                </span>
                Try the app
                <span className="ml-auto text-[9px] font-bold uppercase tracking-[0.14em] text-sage-600">Beta</span>
              </Link>
              <div className="h-px bg-sand-300 my-2" />
              {user ? (
                <Link to="/" className="px-4 py-3 rounded-xl bg-terracotta-500 text-white font-medium text-center">Go to dashboard</Link>
              ) : (
                <>
                  <Link to="/login" className="px-4 py-3 rounded-xl hover:bg-sand-200 text-stoneink-900 font-medium">Sign in</Link>
                  <Link to="/register" className="px-4 py-3 rounded-xl bg-terracotta-500 text-white font-medium text-center">Get started</Link>
                </>
              )}
            </div>
          </div>
        )}
      </header>

      <main className="flex-1">{children}</main>

      <footer className="mt-24 border-t border-sand-300 bg-white" data-testid="marketing-footer">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-14">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-10">
            <div className="col-span-2">
              <Link to="/" className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-2xl bg-terracotta-500 text-white grid place-items-center"><House weight="duotone" size={22} /></div>
                <div>
                  <div className="font-heading text-lg font-semibold text-stoneink-900 leading-none">Nestled</div>
                  <div className="text-xs text-stoneink-500 mt-0.5">a place for everything</div>
                </div>
              </Link>
              <p className="mt-4 text-stoneink-700 max-w-xs text-sm leading-relaxed">
                The little inventory app that helps your home feel calmer. Remember where you put it — even five years from now.
              </p>
            </div>
            <FooterCol title="Product" links={[
              ["Features", "/features"], ["Pricing", "/pricing"], ["FAQ", "/faq"], ["Blog", "/blog"], ["Get the app", "/beta"],
            ]} />
            <FooterCol title="Company" links={[
              ["About", "/about"], ["Contact", "/contact"], ["Privacy", "/privacy"], ["Terms", "/terms"],
            ]} />
          </div>
          <div className="mt-12 pt-6 border-t border-sand-300 flex flex-col sm:flex-row justify-between items-center gap-3 text-xs text-stoneink-500">
            <div>© {new Date().getFullYear()} Nestled — a place for everything.</div>
            <div className="flex items-center gap-4">
              <Link to="/privacy" className="hover:text-terracotta-600">Privacy</Link>
              <Link to="/terms" className="hover:text-terracotta-600">Terms</Link>
              <Link to="/contact" className="hover:text-terracotta-600">Contact</Link>
              {user?.role === "admin" && (
                <Link to="/admin/blog" data-testid="footer-admin-blog-link"
                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border border-sand-300 text-stoneink-700 hover:text-terracotta-600 hover:border-terracotta-500 transition-colors">
                  <span className="text-[10px] font-bold uppercase tracking-[0.14em] text-terracotta-600">Admin</span>
                  <span>Manage blog</span>
                </Link>
              )}
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}

const FooterCol = ({ title, links }) => (
  <div>
    <div className="text-xs font-bold uppercase tracking-[0.18em] text-stoneink-900 mb-3">{title}</div>
    <ul className="space-y-2">
      {links.map(([label, to]) => (
        <li key={to}><Link to={to} className="text-sm text-stoneink-700 hover:text-terracotta-600">{label}</Link></li>
      ))}
    </ul>
  </div>
);
